import React, { useContext, useState, useEffect, useMemo, useRef } from 'react'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { DesignContext } from 'contexts/DesignContext'
import objectPath from 'object-path'
import Title from '../title/Title'

const STICKY_TOP_GAP = 8
const STICKY_UNLOCK_BUFFER = 12
const STICKY_READY_DELAY = 500
const STICKY_READY_MAX_DELAY = 5000
const STICKY_Z_INDEX = 1028

const CustomCollapse = (props) => {
  const designContext = useContext(DesignContext)
  const { isUiChanged, setIsUiChanged, isLoading, isLoadingApi } = designContext
  const uiChanged = objectPath(isUiChanged)
  let { uischema, children } = props
  const isRender = uiChanged.get([uischema.key, 'isRender'])
  const stickyRef = useRef(null)
  const cardRef = useRef(null)
  const headerRef = useRef(null)
  const hasStickyScrollRef = useRef(false)
  const stickyScrollFrameRef = useRef(null)
  const stickyReadyTimerRef = useRef(null)
  const stickyReadyMaxTimerRef = useRef(null)
  const stickyResizeObserverRef = useRef(null)
  const stickyStackRef = useRef(null)
  const stickyStackStyleRef = useRef(null)

  const [isCollpaseOpen, setIsCollapseOpen] = useState(
    () => !!uischema.openCollapse,
  )
  const [isStickyReady, setIsStickyReady] = useState(false)
  const [isStickyLocked, setIsStickyLocked] = useState(false)
  const [stickyTop, setStickyTop] = useState(0)
  const [stickyPosition, setStickyPosition] = useState({
    left: 0,
    width: 0,
  })
  const [headerChange, setHeaderChange] = useState({})
  const [titleChange, setTitleChange] = useState({})
  const [borderChange, setBorderChange] = useState({})
  const isStickyCollapse = !!uischema.stickyCollapse
  const isStickyCollapseLocked = isStickyLocked
  const isCollapseDisplayOpen = isCollpaseOpen
  const stickyCard = isStickyCollapseLocked
    ? {
        position: 'fixed',
        top: stickyTop,
        left: stickyPosition.left,
        width: stickyPosition.width,
        zIndex: STICKY_Z_INDEX,
        background: '#fff',
        border: '1px solid #d8e2ec',
        borderLeft: '4px solid #4099ff',
        borderRadius: '4px',
        boxShadow: '0 10px 18px rgba(31, 41, 55, 0.14)',
        marginTop: 0,
        marginBottom: 0,
        overflow: 'hidden',
        backfaceVisibility: 'hidden',
        transform: 'translateZ(0)',
      }
    : {}

  const handleHover = (isEnter) => {
    let style = isCollapseDisplayOpen ? styleOpen : styleClose

    setTitleChange(style.title)
    setBorderChange(style.border)

    if (isEnter && !isStickyCollapseLocked) {
      setHeaderChange(style.headerHover)
    } else {
      setHeaderChange(style.header)
    }
  }

  const handleCollapseClick = () => {
    setIsCollapseOpen((isCollpaseOpen) => !isCollpaseOpen)
  }

  const getStickyTop = () => {
    let header = document.querySelector('.pcoded-header')
    return header
      ? Math.max(header.getBoundingClientRect().bottom, 0) + STICKY_TOP_GAP
      : 0
  }

  const updateStickyPosition = () => {
    if (!stickyRef.current) return

    let stickyRect = stickyRef.current.getBoundingClientRect()

    setStickyPosition((stickyPosition) => {
      let nextStickyPosition = {
        left: stickyRect.left,
        width: stickyRect.width,
      }

      return JSON.stringify(stickyPosition) ===
        JSON.stringify(nextStickyPosition)
        ? stickyPosition
        : nextStickyPosition
    })
  }

  const clearStickyStack = () => {
    if (!stickyStackRef.current || !stickyStackStyleRef.current) return

    stickyStackRef.current.style.position =
      stickyStackStyleRef.current.position
    stickyStackRef.current.style.zIndex = stickyStackStyleRef.current.zIndex
    stickyStackRef.current = null
    stickyStackStyleRef.current = null
  }

  const getStickyStackElement = () => {
    let stackElement = cardRef.current?.parentElement

    while (stackElement && stackElement !== document.body) {
      let stackStyle = window.getComputedStyle(stackElement)

      if (stackStyle.position !== 'static' && stackStyle.zIndex !== 'auto') {
        return stackElement
      }

      stackElement = stackElement.parentElement
    }

    return cardRef.current?.parentElement || null
  }

  const initialColor = '#f8f9fa'

  const styleOpen = useMemo(
    () => ({
      header: {
        background: uischema.cardColor,
      },
      headerHover: {
        background: uischema.cardColor,
        cursor: 'pointer',
      },
      border: {
        border: '1px solid ' + uischema.cardColor,
        margin: uischema.layoutMargin,
      },
      title: {
        color: uischema.textColor,
      },
    }),
    [uischema.cardColor, uischema.layoutMargin, uischema.textColor],
  )

  const styleClose = useMemo(
    () => ({
      header: {
        background: initialColor,
      },
      headerHover: {
        background: initialColor,
        cursor: 'pointer',
      },
      border: {
        border: '1px solid ' + initialColor,
        margin: uischema.layoutMargin,
      },
      title: {
        color: 'black',
      },
    }),
    [uischema.layoutMargin],
  )

  useEffect(() => {
    setIsCollapseOpen(!!uischema.openCollapse)
  }, [uischema.openCollapse])

  useEffect(() => {
    if (isCollapseDisplayOpen) {
      setHeaderChange(styleOpen.header)
      setTitleChange(styleOpen.title)
      setBorderChange(styleOpen.border)
    } else {
      setHeaderChange(styleClose.header)
      setTitleChange(styleClose.title)
      setBorderChange(styleClose.border)
    }
  }, [isCollapseDisplayOpen, styleClose, styleOpen])

  useEffect(() => {
    if (!isStickyCollapseLocked) {
      clearStickyStack()
      return
    }

    let stackElement = getStickyStackElement()
    if (!stackElement) return

    if (stickyStackRef.current && stickyStackRef.current !== stackElement) {
      clearStickyStack()
    }

    if (!stickyStackStyleRef.current) {
      stickyStackStyleRef.current = {
        position: stackElement.style.position,
        zIndex: stackElement.style.zIndex,
      }
    }

    stickyStackRef.current = stackElement
    if (window.getComputedStyle(stackElement).position === 'static') {
      stackElement.style.position = 'relative'
    }
    stackElement.style.zIndex = STICKY_Z_INDEX

    return clearStickyStack
  }, [isStickyCollapseLocked])

  useEffect(() => {
    const clearStickyReadyWait = () => {
      if (stickyReadyTimerRef.current) {
        window.clearTimeout(stickyReadyTimerRef.current)
        stickyReadyTimerRef.current = null
      }

      if (stickyReadyMaxTimerRef.current) {
        window.clearTimeout(stickyReadyMaxTimerRef.current)
        stickyReadyMaxTimerRef.current = null
      }

      if (stickyResizeObserverRef.current) {
        stickyResizeObserverRef.current.disconnect()
        stickyResizeObserverRef.current = null
      }
    }
    const finishStickyReady = () => {
      clearStickyReadyWait()
      hasStickyScrollRef.current = false
      setIsStickyReady(true)
    }
    const waitStickyReady = () => {
      clearStickyReadyWait()
      stickyReadyTimerRef.current = window.setTimeout(
        finishStickyReady,
        STICKY_READY_DELAY,
      )
      stickyReadyMaxTimerRef.current = window.setTimeout(
        finishStickyReady,
        STICKY_READY_MAX_DELAY,
      )

      if (window.ResizeObserver) {
        let stickyContainer =
          stickyRef.current?.closest('.pcoded-main-container') || document.body

        stickyResizeObserverRef.current = new window.ResizeObserver(() => {
          if (stickyReadyTimerRef.current) {
            window.clearTimeout(stickyReadyTimerRef.current)
          }

          stickyReadyTimerRef.current = window.setTimeout(
            finishStickyReady,
            STICKY_READY_DELAY,
          )
        })
        stickyResizeObserverRef.current.observe(stickyContainer)
      }
    }
    const handleLoad = () => {
      waitStickyReady()
    }

    setIsStickyReady(false)

    if (!isLoading && !isLoadingApi) {
      if (document.readyState === 'complete') {
        waitStickyReady()
      } else {
        window.addEventListener('load', handleLoad)
      }
    }

    return () => {
      window.removeEventListener('load', handleLoad)
      clearStickyReadyWait()
    }
  }, [isLoading, isLoadingApi])

  useEffect(() => {
    const updateStickyLocked = (allowLock = hasStickyScrollRef.current) => {
      if (!stickyRef.current) {
        setIsStickyLocked(false)
        return
      }

      if (!isStickyReady) {
        setIsStickyLocked(false)
        return
      }

      let isStickyEnabled = isStickyCollapse

      if (!isStickyEnabled) {
        setIsStickyLocked(false)
        return
      }

      let top = getStickyTop()
      let scrollTop = window.pageYOffset || document.documentElement.scrollTop
      let headerRect = headerRef.current
        ? headerRef.current.getBoundingClientRect()
        : stickyRef.current.getBoundingClientRect()
      let headerBottom = headerRect.bottom

      setStickyTop((stickyTop) => (stickyTop === top ? stickyTop : top))
      updateStickyPosition()
      setIsStickyLocked((isStickyLocked) => {
        let nextStickyLocked = false

        if (allowLock && scrollTop > 0) {
          nextStickyLocked = isStickyLocked
            ? headerBottom <= top + STICKY_UNLOCK_BUFFER
            : headerBottom <= top
        }

        return isStickyLocked === nextStickyLocked
          ? isStickyLocked
          : nextStickyLocked
      })
    }
    const requestStickyUpdate = (allowLock) => {
      if (stickyScrollFrameRef.current) return

      stickyScrollFrameRef.current = window.requestAnimationFrame(() => {
        stickyScrollFrameRef.current = null
        updateStickyLocked(allowLock)
      })
    }
    const handleScroll = () => {
      hasStickyScrollRef.current = true
      requestStickyUpdate(true)
    }
    const handleResize = () => {
      requestStickyUpdate()
    }

    updateStickyLocked(false)
    window.addEventListener('scroll', handleScroll, true)
    window.addEventListener('resize', handleResize)

    return () => {
      if (stickyScrollFrameRef.current) {
        window.cancelAnimationFrame(stickyScrollFrameRef.current)
      }
      window.removeEventListener('scroll', handleScroll, true)
      window.removeEventListener('resize', handleResize)
    }
  }, [isStickyCollapse, isStickyReady, isCollpaseOpen])

  useEffect(() => {
    if (isRender) {
      uiChanged.del(uischema.key)
      setIsUiChanged(uiChanged.get(''))
    }
  }, [isRender])

  return (
    <>
      <div ref={stickyRef} style={{ height: 0, width: '100%' }} />
      {isStickyCollapseLocked && (
        <div
          className="card my-2"
          style={{
            ...borderChange,
            ...stickyCard,
            minWidth: uischema.minWidth,
            maxWidth: uischema.maxWidth,
          }}
        >
          <div
            className="card-header p-3"
            style={{
              ...headerChange,
              alignItems: 'center',
              background: '#fff',
              border: 0,
              cursor: 'default',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <h5
              className="card-title"
              style={{
                ...titleChange,
                color: '#1f2933',
                marginBottom: 0,
              }}
            >
              <Title {...props} />
            </h5>
          </div>
        </div>
      )}
      <Accordion
        ref={cardRef}
        id={`${uischema.key}-card`}
        key={uischema.key}
        expanded={isCollapseDisplayOpen}
        disableGutters
        elevation={0}
        className="card my-2"
        style={{
          ...borderChange,
          background: '#fff',
          minWidth: uischema.minWidth,
          maxWidth: uischema.maxWidth,
          overflowAnchor: 'none',
        }}
        sx={{
          '&:before': {
            display: 'none',
          },
        }}
      >
        <AccordionSummary
          ref={headerRef}
          style={{
            ...headerChange,
          }}
          className={`card-header p-3`}
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`${uischema.key}-collapse`}
          id={`${uischema.key}-header`}
          onClick={handleCollapseClick}
          onMouseEnter={() => handleHover(true)}
          onMouseLeave={() => handleHover(false)}
          sx={{
            minHeight: 'auto',
            '&.Mui-expanded': {
              minHeight: 'auto',
            },
            '& .MuiAccordionSummary-content': {
              margin: 0,
            },
            '& .MuiAccordionSummary-content.Mui-expanded': {
              margin: 0,
            },
          }}
        >
          <h5
            className="card-title"
            style={{
              ...titleChange,
            }}
          >
            <Title {...props} />
          </h5>
        </AccordionSummary>
        <AccordionDetails
          id={`${uischema.key}-collapse`}
          style={{
            background: '#fff',
            padding: 0,
          }}
        >
          <div
            id={`${uischema.key}-card-body`}
            style={{
              background: '#fff',
              padding: uischema.layoutPadding ?? '.5rem',
            }}
          >
            {children}
          </div>
        </AccordionDetails>
      </Accordion>
    </>
  )
}

export default CustomCollapse
